import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { faqLd, breadcrumbLd, webAppLd, productLd } from "../components/Seo";
import {
  fetchForexRates,
  fetchForexCards,
  fetchCashAdvice,
  checkBargain,
} from "../lib/api";
import { track } from "../lib/analytics";
import {
  CurrencyInr,
  ArrowsLeftRight,
  ArrowRight,
  Scales,
  CreditCard,
  Wallet,
  Star,
  Check,
  Warning,
  Sparkle,
  Receipt,
  Money,
  CaretRight,
  Lightning,
  ArrowsClockwise,
  HandCoins,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TIPS = [0, 5, 10, 15, 18, 20];

const VERDICT_META = {
  steal: { color: "#3DB68B", bg: "#3DB68B", label: "Steal", emoji: "💎" },
  fair: { color: "#3DB68B", bg: "#2A7FB8", label: "Fair", emoji: "✅" },
  overpriced: { color: "#F4B942", bg: "#F4B942", label: "Overpriced", emoji: "⚠️" },
  ripoff: { color: "#E0457B", bg: "#E0457B", label: "Ripoff", emoji: "🚨" },
};

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtNum = (n, d = 2) => Number(n || 0).toFixed(d);

export default function Forex() {
  const navigate = useNavigate();
  const [rates, setRates] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loadingRates, setLoadingRates] = useState(true);

  const [cards, setCards] = useState([]);
  const [advice, setAdvice] = useState([]);

  // Converter
  const [amountInr, setAmountInr] = useState(10000);
  const [targetCcy, setTargetCcy] = useState("THB");
  const [billAmount, setBillAmount] = useState(1000);
  const [tipPct, setTipPct] = useState(10);
  const [tipCcy, setTipCcy] = useState("THB");

  // Bargain check
  const [bcCountry, setBcCountry] = useState("thailand");
  const [bcItem, setBcItem] = useState("5-minute tuk-tuk ride from Khao San to Wat Pho");
  const [bcPrice, setBcPrice] = useState(800);
  const [bcCcy, setBcCcy] = useState("THB");
  const [bcLoading, setBcLoading] = useState(false);
  const [bcResult, setBcResult] = useState(null);

  // Country picker for cash advice
  const [adviceCountry, setAdviceCountry] = useState("thailand");

  useEffect(() => {
    (async () => {
      try {
        const [r, c, a] = await Promise.all([
          fetchForexRates(),
          fetchForexCards(),
          fetchCashAdvice(),
        ]);
        setRates(r.rates || {});
        setCurrencies(r.currencies || []);
        setFetchedAt(r.fetched_at);
        setCards(c);
        setAdvice(a);
      } catch (e) {
        toast.error("Couldn't load forex data");
      } finally {
        setLoadingRates(false);
      }
    })();
  }, []);

  const adviceForCountry = useMemo(
    () => advice.find((a) => a.country_slug === adviceCountry),
    [advice, adviceCountry],
  );

  const convertedAmount = useMemo(() => {
    const r = rates[targetCcy];
    if (!r) return 0;
    return Number(amountInr || 0) * r;
  }, [amountInr, targetCcy, rates]);

  const tipBreakdown = useMemo(() => {
    const r = rates[tipCcy];
    const base = Number(billAmount || 0);
    const tip = (base * Number(tipPct || 0)) / 100;
    const total = base + tip;
    return {
      tip,
      total,
      tipInr: r ? tip / r : 0,
      totalInr: r ? total / r : 0,
    };
  }, [billAmount, tipPct, tipCcy, rates]);

  const runBargain = async () => {
    if (!bcItem || !bcPrice) {
      toast.error("Fill the item and asking price");
      return;
    }
    track("bargain_check_started", { country: bcCountry, currency: bcCcy });
    setBcLoading(true);
    setBcResult(null);
    try {
      const data = await checkBargain({
        country_slug: bcCountry,
        item: bcItem,
        asking_price: Number(bcPrice),
        currency: bcCcy,
      });
      setBcResult(data);
      track("bargain_check_completed", { country: bcCountry, verdict: data.verdict });
      setTimeout(
        () =>
          document
            .getElementById("bargain-result")
            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        160,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't analyse this price. Try again.");
    } finally {
      setBcLoading(false);
    }
  };

  // SEO structured data: FAQ + WebApplication + Product schemas.
  // FAQ schema targets long-tail queries like "best forex card for indians",
  // "is niyo global free", "how much cash to carry to thailand".
  const structuredData = useMemo(() => {
    const out = [
      webAppLd({
        name: "Flycher Money & Forex Center",
        description:
          "Live INR converter, AI bargain check, forex card comparison, and cash-vs-card advice for Indian travelers.",
        path: "/forex",
      }),
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Forex & Money", path: "/forex" },
      ]),
      faqLd([
        {
          question: "What is the best forex card for Indians traveling abroad?",
          answer:
            "Niyo Global (0% markup, free) for Southeast Asia and day-to-day spending. Wise (0.4% markup, ₹500 issuance) for multi-country trips. IndusInd Pioneer Heritage (1.5% markup, ₹50,000 annual fee) for premium travelers who want lounge access and trip insurance.",
        },
        {
          question: "How much cash should I carry abroad from India?",
          answer:
            "It depends on the country. Cash-heavy: Vietnam, Laos, Nepal — carry 60-85% as cash (₹15,000-₹25,000 worth). Card-friendly: Singapore, UAE, Japan — only 10-50% cash needed (₹5,000-₹12,000 worth). Always carry some cash as backup.",
        },
        {
          question: "Are there hidden fees on Indian debit cards abroad?",
          answer:
            "Yes — regular HDFC/SBI/ICICI debit cards charge ~3-3.5% FX markup hidden in the rate, plus ₹150-200 flat fee per ATM withdrawal abroad. Use Niyo Global or Wise instead to save 3-4% on every transaction.",
        },
        {
          question: "Do I need to tip in Japan?",
          answer:
            "No. Tipping is considered rude in Japan. Round up at most. Tipping etiquette varies wildly: USA mandates 18-22%, Southeast Asia expects 10%, Australia and Europe don't expect tips.",
        },
        {
          question: "Is the asking price fair? How does the bargain check work?",
          answer:
            "Paste the country, what you're buying, the asking price, and the local currency. Our AI compares it against typical local rates and tells you whether it's fair, overpriced, or a ripoff, along with the fair INR range and a bargaining script.",
        },
      ]),
      // Product schema for top 3 cards (rich snippet eligible)
      ...cards.slice(0, 3).map((c) =>
        productLd({
          name: c.name,
          description: c.best_for,
          brand: c.issuer,
          rating: c.rating,
        }),
      ),
    ];
    return out;
  }, [cards]);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F0]">
      <Seo
        title="Money & Forex for Indians — INR converter, best forex card, bargain check"
        description="Live INR ↔ converter, Indian forex card comparison (Niyo, Wise, IndusInd, HDFC, Axis), AI bargain check ('Is 800 baht fair for a tuk-tuk?'), and cash-vs-card advice for 18 countries."
        path="/forex"
        jsonLd={structuredData}
      />
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-5 sm:px-8 pt-28 pb-20">
        <span className="kicker">— Money & forex center</span>
        <h1 className="font-serif text-4xl sm:text-6xl mt-2 text-slate-900 leading-tight">
          Don't lose money to{" "}
          <span className="italic" style={{ color: "#3DB68B" }}>
            hidden FX fees.
          </span>
        </h1>
        <p className="text-slate-600 mt-4 max-w-2xl">
          Live INR conversion · AI fairness check on local prices · India-specific forex card
          comparison · cash-vs-card splits per country.
        </p>
        {fetchedAt && (
          <p className="mt-2 text-[11px] font-mono text-slate-400 inline-flex items-center gap-1.5">
            <ArrowsClockwise size={11} weight="bold" />
            Rates refreshed {new Date(fetchedAt).toLocaleString("en-IN")}
          </p>
        )}

        {/* SECTION 1 — CONVERTER + TIP */}
        <section data-testid="forex-converter" className="mt-10 grid lg:grid-cols-2 gap-5">
          {/* Converter */}
          <article className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-6">
            <div className="flex items-center gap-2">
              <ArrowsLeftRight size={18} weight="duotone" color="#2A7FB8" />
              <span className="kicker">— Live INR ↔ converter</span>
            </div>
            <div className="mt-4 grid grid-cols-12 gap-3 items-end">
              <label className="col-span-7 flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  You spend (INR)
                </span>
                <input
                  type="number"
                  min={0}
                  data-testid="conv-inr"
                  value={amountInr}
                  onChange={(e) => setAmountInr(e.target.value)}
                  className="input-base h-12 text-base font-mono"
                />
              </label>
              <label className="col-span-5 flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  Currency
                </span>
                <select
                  data-testid="conv-ccy"
                  value={targetCcy}
                  onChange={(e) => setTargetCcy(e.target.value)}
                  className="select-base h-12 text-base"
                >
                  {currencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} · {c.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div
              className="mt-5 rounded-xl p-5 text-white"
              style={{ background: "linear-gradient(135deg, #2A7FB8 0%, #3DB68B 100%)" }}
            >
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/85">
                You'll get
              </span>
              <p className="font-serif text-3xl sm:text-4xl mt-1 font-bold leading-tight">
                {fmtNum(convertedAmount, targetCcy === "VND" || targetCcy === "IDR" || targetCcy === "KRW" || targetCcy === "LAK" ? 0 : 2)}{" "}
                <span className="text-base font-normal text-white/85">{targetCcy}</span>
              </p>
              <p className="text-[11px] mt-2 text-white/85 font-mono">
                Live mid-market · cards typically charge 0–3.5% on top
              </p>
            </div>
            {/* Quick chips */}
            <div className="mt-3 flex flex-wrap gap-2">
              {[5000, 10000, 25000, 50000, 100000].map((v) => (
                <button
                  key={v}
                  data-testid={`conv-chip-${v}`}
                  onClick={() => setAmountInr(v)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition ${
                    Number(amountInr) === v
                      ? "border-[#2A7FB8] bg-[#EAF4FB] text-[#2A7FB8]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {fmtINR(v).replace("₹", "₹")}
                </button>
              ))}
            </div>
          </article>

          {/* Tip Calculator */}
          <article className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-6">
            <div className="flex items-center gap-2">
              <Receipt size={18} weight="duotone" color="#FF7E4D" />
              <span className="kicker">— Tip calculator</span>
            </div>
            <div className="mt-4 grid grid-cols-12 gap-3 items-end">
              <label className="col-span-7 flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  Bill amount
                </span>
                <input
                  type="number"
                  min={0}
                  data-testid="tip-bill"
                  value={billAmount}
                  onChange={(e) => setBillAmount(e.target.value)}
                  className="input-base h-12 text-base font-mono"
                />
              </label>
              <label className="col-span-5 flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  Currency
                </span>
                <select
                  data-testid="tip-ccy"
                  value={tipCcy}
                  onChange={(e) => setTipCcy(e.target.value)}
                  className="select-base h-12 text-base"
                >
                  {currencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-4 mb-2">
              Tip %
            </p>
            <div className="flex flex-wrap gap-2">
              {TIPS.map((t) => (
                <button
                  key={t}
                  data-testid={`tip-pct-${t}`}
                  onClick={() => setTipPct(t)}
                  className={`px-3.5 py-1.5 rounded-full text-xs border transition ${
                    tipPct === t
                      ? "border-transparent text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                  style={tipPct === t ? { background: "#FF7E4D" } : {}}
                >
                  {t === 0 ? "No tip" : `${t}%`}
                </button>
              ))}
            </div>
            <div className="mt-4 rounded-xl bg-[#FFF1E8] border border-[#FF7E4D]/20 p-4 space-y-1.5 font-mono text-sm">
              <Row label="Tip" v={`${fmtNum(tipBreakdown.tip, 0)} ${tipCcy}`} sub={fmtINR(tipBreakdown.tipInr)} />
              <Row label="Total" v={`${fmtNum(tipBreakdown.total, 0)} ${tipCcy}`} sub={fmtINR(tipBreakdown.totalInr)} bold />
            </div>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
              💡 Tipping norms vary wildly — Japan: don't tip. USA: 18–22% mandatory. SEA: 10% common. See cash-advice section below.
            </p>
          </article>
        </section>

        {/* SECTION 2 — BARGAIN CHECK */}
        <section
          id="bargain"
          data-testid="forex-bargain"
          className="mt-10 rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-6"
        >
          <div className="flex items-center gap-2">
            <Scales size={18} weight="duotone" color="#E0457B" />
            <span className="kicker">— "Is this fair?" bargain check (AI)</span>
          </div>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            They quoted you a price — paste it here. Our AI will tell you the fair range, an INR
            equivalent, and a script to negotiate down.
          </p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-3">
            <label className="md:col-span-4 flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Country
              </span>
              <select
                value={bcCountry}
                onChange={(e) => {
                  setBcCountry(e.target.value);
                  // Auto-switch ccy to country's primary
                  const a = advice.find((x) => x.country_slug === e.target.value);
                  if (a) {
                    // pick first currency hint match from currencies list
                    const guess = currencies.find((c) =>
                      c.country_hint?.toLowerCase().includes(e.target.value.replace("-", " ")),
                    );
                    if (guess) setBcCcy(guess.code);
                  }
                }}
                data-testid="bc-country"
                className="select-base h-12 text-base"
              >
                {advice.map((a) => (
                  <option key={a.country_slug} value={a.country_slug}>
                    {a.country_slug}
                  </option>
                ))}
              </select>
            </label>
            <label className="md:col-span-4 flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                What are they charging for?
              </span>
              <input
                type="text"
                data-testid="bc-item"
                value={bcItem}
                onChange={(e) => setBcItem(e.target.value)}
                placeholder="e.g. 5-min tuk-tuk, 1L water, 30-min massage"
                className="input-base h-12 text-base"
              />
            </label>
            <label className="md:col-span-2 flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Asking price
              </span>
              <input
                type="number"
                min={0}
                data-testid="bc-price"
                value={bcPrice}
                onChange={(e) => setBcPrice(e.target.value)}
                className="input-base h-12 text-base font-mono"
              />
            </label>
            <label className="md:col-span-2 flex flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Currency
              </span>
              <select
                value={bcCcy}
                onChange={(e) => setBcCcy(e.target.value)}
                data-testid="bc-ccy"
                className="select-base h-12 text-base"
              >
                {currencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}
                  </option>
                ))}
              </select>
            </label>
            <div className="md:col-span-12 flex justify-end">
              <button
                type="button"
                onClick={runBargain}
                disabled={bcLoading}
                data-testid="bc-run"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white shadow-lg disabled:opacity-50"
                style={{ background: "#E0457B" }}
              >
                <Scales size={16} weight="bold" />
                {bcLoading ? "Checking…" : "Check if it's fair"}
              </button>
            </div>
          </div>

          {bcLoading && (
            <div className="mt-6 rounded-xl bg-slate-50 p-6 text-center">
              <div className="w-8 h-8 mx-auto rounded-full border-2 border-[#E0457B] border-t-transparent animate-spin" />
              <p className="mt-3 text-xs text-slate-500 font-mono uppercase tracking-wider">
                Asking AI…
              </p>
            </div>
          )}

          {bcResult && !bcLoading && <BargainResult data={bcResult} />}
        </section>

        {/* SECTION 3 — FOREX CARDS */}
        <section data-testid="forex-cards" className="mt-12">
          <div className="flex items-center gap-2">
            <CreditCard size={20} weight="duotone" color="#7C6CE0" />
            <span className="kicker">— Best Indian forex cards (ranked)</span>
          </div>
          <h2 className="font-serif text-2xl sm:text-4xl mt-2 text-slate-900">
            Compare the cards Indian travelers actually use.
          </h2>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Sorted best-to-worst by total cost (FX markup + ATM fees + annual fee). Apply links go
            direct to issuer pages.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {cards.map((c, i) => (
              <CardItem key={c.id} c={c} rank={i + 1} />
            ))}
          </div>
        </section>

        {/* SECTION 4 — CASH VS CARD */}
        <section data-testid="forex-cash" className="mt-12">
          <div className="flex items-center gap-2">
            <Wallet size={20} weight="duotone" color="#3DB68B" />
            <span className="kicker">— Cash vs card · per country</span>
          </div>
          <h2 className="font-serif text-2xl sm:text-4xl mt-2 text-slate-900">
            How much rupee-worth cash should you carry?
          </h2>

          {/* Country picker */}
          <div className="mt-5 flex flex-wrap gap-2">
            {advice.map((a) => {
              const active = adviceCountry === a.country_slug;
              return (
                <button
                  key={a.country_slug}
                  data-testid={`cash-pick-${a.country_slug}`}
                  onClick={() => setAdviceCountry(a.country_slug)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition capitalize ${
                    active
                      ? "border-transparent text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                  style={active ? { background: "#3DB68B" } : {}}
                >
                  {a.country_slug.replace("-", " ")}
                </button>
              );
            })}
          </div>

          {adviceForCountry && <CashAdviceCard a={adviceForCountry} />}
        </section>

        {/* CTA */}
        <section className="mt-12">
          <div className="rounded-2xl bg-slate-900 text-white p-6 sm:p-8 flex items-start sm:items-center gap-5 flex-col sm:flex-row justify-between">
            <div>
              <span className="kicker text-white/70">— Pair this with</span>
              <h3 className="font-serif text-2xl sm:text-3xl mt-2">
                Build the trip · estimate the budget · check the visa.
              </h3>
              <p className="text-sm text-slate-300 mt-1.5 max-w-xl">
                Use Flycher's other tools to plan the rest of your journey.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <button
                onClick={() => navigate("/budget")}
                className="px-4 py-2.5 rounded-full text-xs font-semibold bg-white text-slate-900 hover:bg-slate-100"
              >
                Budget
              </button>
              <button
                onClick={() => navigate("/visa")}
                className="px-4 py-2.5 rounded-full text-xs font-semibold bg-white/15 text-white hover:bg-white/25"
              >
                Visa
              </button>
              <button
                onClick={() => navigate("/itinerary")}
                className="px-4 py-2.5 rounded-full text-xs font-semibold text-white"
                style={{ background: "#FF7E4D" }}
              >
                Plan itinerary <ArrowRight size={12} weight="bold" className="inline ml-1" />
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Row({ label, v, sub, bold }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 ${bold ? "text-base font-bold" : ""}`}>
      <span className="text-slate-700">{label}</span>
      <span className="text-right">
        <span className="text-slate-900">{v}</span>
        {sub && <span className="block text-[11px] text-slate-500 font-mono">{sub}</span>}
      </span>
    </div>
  );
}

function BargainResult({ data }) {
  const meta = VERDICT_META[data.verdict] || VERDICT_META.fair;
  return (
    <div id="bargain-result" data-testid="bc-result" className="mt-7 space-y-4">
      {/* Verdict hero */}
      <div
        className="rounded-2xl p-5 sm:p-7 text-white shadow-xl"
        style={{ background: `linear-gradient(135deg, ${meta.bg} 0%, ${meta.bg}CC 100%)` }}
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="text-5xl leading-none">{data.verdict_emoji || meta.emoji}</div>
          <div className="flex-1 min-w-[200px]">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/85">
              Verdict · {data.country}
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl mt-1 leading-tight">
              {data.headline || meta.label}
            </h3>
            <p className="mt-2 text-sm sm:text-base text-white/95 leading-relaxed">
              {data.explanation}
            </p>
          </div>
          <div className="text-right">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/75">
              Overpay
            </span>
            <div className="font-serif text-4xl sm:text-5xl font-bold">
              {data.overpay_pct > 0 ? "+" : ""}
              {data.overpay_pct}%
            </div>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl bg-white/15 backdrop-blur p-3.5">
            <p className="text-[10px] font-mono uppercase tracking-wider text-white/85">
              They asked
            </p>
            <p className="font-serif text-xl mt-0.5 font-semibold">
              {data.asking_price} {data.currency}{" "}
              {data.asking_inr && <span className="text-sm text-white/80">≈ ₹{data.asking_inr.toLocaleString("en-IN")}</span>}
            </p>
          </div>
          <div className="rounded-xl bg-white/15 backdrop-blur p-3.5">
            <p className="text-[10px] font-mono uppercase tracking-wider text-white/85">
              Fair range
            </p>
            <p className="font-serif text-xl mt-0.5 font-semibold">
              {data.fair_price_min}–{data.fair_price_max} {data.currency}{" "}
              {data.fair_price_inr_min && (
                <span className="text-sm text-white/80">
                  ≈ ₹{data.fair_price_inr_min.toLocaleString("en-IN")}–₹
                  {data.fair_price_inr_max?.toLocaleString("en-IN")}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Bargaining script */}
      {data.bargaining_script && (
        <div className="rounded-2xl bg-[#EAF4FB] border border-[#2A7FB8]/20 p-5">
          <div className="flex items-center gap-2">
            <HandCoins size={16} weight="duotone" color="#2A7FB8" />
            <span className="kicker">— Bargaining script</span>
          </div>
          <p className="mt-2 text-sm text-slate-800 italic leading-relaxed">
            "{data.bargaining_script}"
          </p>
        </div>
      )}

      {/* Red flags + tip */}
      <div className="grid sm:grid-cols-2 gap-3">
        {data.red_flags?.length > 0 && (
          <div className="rounded-2xl bg-[#FEF3F2] border border-[#E0457B]/15 p-5">
            <div className="flex items-center gap-2">
              <Warning size={16} weight="fill" color="#E0457B" />
              <span className="kicker">— Red flags</span>
            </div>
            <ul className="mt-2 space-y-1.5">
              {data.red_flags.map((f, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-start gap-2 leading-relaxed">
                  <span className="inline-block w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-[#E0457B]" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {data.tip && (
          <div className="rounded-2xl bg-[#FFF1E8] border border-[#FF7E4D]/20 p-5">
            <div className="flex items-center gap-2">
              <Lightning size={16} weight="fill" color="#FF7E4D" />
              <span className="kicker">— Pro tip</span>
            </div>
            <p className="mt-2 text-sm text-slate-800 leading-relaxed">{data.tip}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CardItem({ c, rank }) {
  const tier =
    c.fx_markup_pct === 0
      ? { color: "#3DB68B", label: "0% markup" }
      : c.fx_markup_pct < 1
      ? { color: "#2A7FB8", label: `${c.fx_markup_pct}% markup` }
      : c.fx_markup_pct < 2
      ? { color: "#F4B942", label: `${c.fx_markup_pct}% markup` }
      : { color: "#E0457B", label: `${c.fx_markup_pct}% markup` };

  return (
    <article
      data-testid={`forex-card-${c.id}`}
      className="rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-md transition-shadow flex flex-col"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
            #{rank} · {c.issuer}
          </span>
          <h3 className="font-serif text-xl mt-0.5 text-slate-900 leading-tight">{c.name}</h3>
          <p className="text-[11px] text-slate-500 mt-1">{c.type}</p>
        </div>
        <span
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold flex-shrink-0"
          style={{ background: tier.color + "1A", color: tier.color }}
        >
          {tier.label}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <Mini label="ATM" value={c.atm_withdrawal_fee} />
        <Mini label="Annual fee" value={c.annual_fee_inr ? fmtINR(c.annual_fee_inr) : "Free"} />
      </div>

      <p className="text-[11px] text-slate-500 mt-2.5 italic">Best for: {c.best_for}</p>

      <div className="mt-3">
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Perks</p>
        <ul className="mt-1.5 space-y-1">
          {c.perks.map((p, i) => (
            <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5 leading-relaxed">
              <Check size={11} weight="bold" color="#3DB68B" className="mt-1 flex-shrink-0" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>

      {c.watchouts?.length > 0 && (
        <div className="mt-2.5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Watch out</p>
          <ul className="mt-1.5 space-y-1">
            {c.watchouts.map((w, i) => (
              <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5 leading-relaxed">
                <Warning size={11} weight="fill" color="#F4B942" className="mt-1 flex-shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-xs">
          <Star size={12} weight="fill" color="#F4B942" />
          <span className="font-mono">{c.rating}</span>
          <span className="text-slate-400">/5</span>
        </div>
        {c.apply_url && (
          <a
            href={c.apply_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`forex-apply-${c.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#7C6CE0] hover:underline"
          >
            Apply
            <CaretRight size={11} weight="bold" />
          </a>
        )}
      </div>
    </article>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <p className="text-[9px] font-mono uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-[11px] text-slate-700 leading-tight mt-0.5">{value}</p>
    </div>
  );
}

function CashAdviceCard({ a }) {
  return (
    <article
      data-testid={`cash-advice-${a.country_slug}`}
      className="mt-5 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden"
    >
      <div
        className="p-5 sm:p-6 text-white"
        style={{ background: "linear-gradient(135deg, #3DB68B 0%, #2A7FB8 100%)" }}
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/85 capitalize">
          {a.country_slug.replace("-", " ")}
        </span>
        <h3 className="font-serif text-2xl sm:text-4xl mt-1 leading-tight">{a.cash_split}</h3>
        <p className="mt-2 text-sm sm:text-base text-white/95">
          Carry approx <strong>{a.cash_for_indian_inr}</strong> worth of cash.
        </p>
      </div>
      <div className="p-5 sm:p-6 grid sm:grid-cols-2 gap-4">
        <Advice Icon={Money} color="#FF7E4D" label="Tipping norms" value={a.tipping} />
        <Advice Icon={Wallet} color="#3DB68B" label="ATM tip" value={a.atm_tip} />
        <Advice
          Icon={CreditCard}
          color="#2A7FB8"
          label="Card acceptance"
          value={a.card_acceptance}
        />
        <Advice
          Icon={Sparkle}
          color="#7C6CE0"
          label="Pro tip for Indians"
          value="Use Niyo Global or Wise card for the lowest markup. Carry a backup forex card too."
        />
      </div>
    </article>
  );
}

function Advice({ Icon, color, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-lg grid place-items-center flex-shrink-0"
        style={{ background: color + "1A", color }}
      >
        <Icon size={16} weight="duotone" />
      </div>
      <div>
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-sm text-slate-700 mt-0.5 leading-relaxed">{value}</p>
      </div>
    </div>
  );
}
